<template>
  <div
    class="edit-ideas height-all flex"
    :class="{
      'edit-ideas-mode': renderData.editMode,
      [`edit-ideas--${createRenderType}`]: true,
    }"
  >
    <div class="edit-ideas-input flex1 height-0">
      <textarea
        ref="refideaTextarea"
        :placeholder="props.placeholder"
        @input="handleInput"
        @change="handleChange"
        @blur="handleBlur"
        @focus="handleFocus"
        @click="handleClick"
        :value="renderData.idea"
        :readonly="props.isLoading"
        @compositionstart="handleCompositionStart"
        @compositionend="handleCompositionEnd"
      >
      </textarea>
    </div>
    <div class="edit-ideas-bottom flex">
      <div
        class="edit-ideas-count"
        :class="{
          'edit-ideas-count-error': renderData.count >= MAX_IDEA_COUNT,
        }"
      >
        {{ renderData.count }} / {{ MAX_IDEA_COUNT }}
      </div>
      <div class="edit-ideas-random">
        <IconButton
          v-if="renderData.linkurl != ''"
          size="32px"
          icoSize="16px"
          :loading="renderData.randomLoading"
          backgroundColor="rgb(255, 255, 255, 0.1)"
          hoverBackgroundColor="rgb(255, 255, 255, 0.2)"
          @onClick="handleRandom"
        >
          <img src="@/assets/img/dice-random-ico.png" />
        </IconButton>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  defineProps,
  defineEmits,
  computed,
  readonly,
  reactive,
  watch,
  defineExpose,
  onMounted,
  onUpdated,
  onUnmounted,
  nextTick,
  onBeforeUnmount,
  getCurrentInstance,
} from "vue"
import { storeToRefs } from "pinia"

import globalConfig, { getGuidanceJsonDataUrl } from "@/config/config.js"
import tools from "@/utils/tools.js"
import { getObjectStorageFullUrl } from "@/lib/objectStorage/objectStorage.js"

import IconButton from "@/components/basic/button/IconButton.vue"

import sseGenerateApi from "@/api/api/apiSse.js"
import feedListApi from "@/api/api/feedListApi.js"

import useCreateLayoutStore from "@/store/create/createLayoutStore.js"
import useCreateEasyModeStore from "@/store/create/createEasyModeStore.js"
import useUserDataStore from "@/store/userDataStore.js"
const createEasyModeStore = useCreateEasyModeStore()
const createLayoutStore = useCreateLayoutStore()
const userDataStore = useUserDataStore()

const { createRenderType } = storeToRefs(createLayoutStore)
const { $$language, $$t, $$tm, $$tValue } = getCurrentInstance().appContext?.config.globalProperties.$i18n ?? {}

const MAX_IDEA_COUNT = 300
const refideaTextarea = ref(null)
const props = defineProps({
  placeholder: {
    type: String,
    default: "",
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  idea: {
    type: String,
    default: "",
  },
})
const emits = defineEmits([
  "input",
  "change",
  "countChange",
  "input:idea",
  "blur",
  "focus",
  "cursorPositionChange",
  "handleRandom",
])

const stateData = {
  separatorReg: `，。、；！？‘【】、=-·~！@#￥%……&*（）——+「」“：》!?,./;'[]\=-)(*&^%$#@!~>?:"{}|｜¥~～·`,
  separator: $$language() == "cn" ? "，" : ", ",
}
const renderData = reactive({
  idea: "",
  count: 0,
  editMode: false,
  randomLoading: false,
})

const handleInputChangeDebounceCall = tools.debounce(() => {
  const globalProperties = (
    getCurrentInstance() || window?.$getVueCurrentInstance?.()
  ).appContext?.config?.globalProperties
  globalProperties?.logCustomEvent(
    "create_inputbox",
    {
      mode: "1",
      tab_stare: "song",
      source: "basic",
      control: "prompt",
    },
    "input"
  )
}, 800)

const handleClick = () => {}
const handleCompositionStart = () => {
  renderData.inputing = true
}

const handleCompositionEnd = (e) => {
  renderData.inputing = false
  handleInput(e)
}
const handleInput = (e) => {
  if (!renderData.inputing) {
    e.target.value = e.target.value.slice(0, MAX_IDEA_COUNT)
    renderData.idea = e.target.value
    renderData.count = renderData.idea.length
    emits("input", renderData.idea)
    emits("countChange", renderData.count)
    handleInputChangeDebounceCall()
  }
}

const handleFocus = (e) => {
  const globalProperties = (
    getCurrentInstance() || window?.$getVueCurrentInstance?.()
  ).appContext?.config?.globalProperties
  renderData.editMode = true
  emits("focus", renderData.idea)
  globalProperties?.logClickEvent("create_inputbox", {
    tab_stare: "song",
    source: "basic",
    control: "prompt",
    mode: "1",
    type: userDataStore.isLogin ? "sign_in" : "not_sign_in",
  })
}

const handleBlur = (e) => {
  renderData.editMode = false
  emits("blur", renderData.idea)
}

const handleChange = () => {
  renderData.count = renderData.idea.length
  emits("change", renderData.idea)
  emits("countChange", renderData.count)
}

const setAddRenderIdea = (data) => {
  data = data ?? ""
  if (!renderData.idea?.replace(/ {0,}\n{0,}/g, "")) {
    renderData.idea = renderData.idea + data
  } else {
    if (
      stateData.separatorReg.includes(
        renderData.idea.replace(/ +\n+/g, "").slice(-1)
      )
    ) {
      if (renderData.idea?.endsWith(" ")) {
        renderData.idea = renderData.idea + data
      } else {
        renderData.idea = renderData.idea + " " + data
      }
    } else {
      if (/( {1,}|\n{1,})$/.test(renderData.idea)) {
        renderData.idea = renderData.idea + data
      } else {
        renderData.idea = renderData.idea + stateData.separator + data
      }
    }
  }

  if (renderData.idea?.length > MAX_IDEA_COUNT) {
    renderData.idea = renderData.idea?.slice(0, MAX_IDEA_COUNT)
    return true
  }
}

const handleRandom = async () => {
  const globalProperties = (
    getCurrentInstance() || window?.$getVueCurrentInstance?.()
  ).appContext?.config?.globalProperties
  renderData.randomLoading = true
  feedListApi
    .getPromptRandomData({
      // prompt: renderData.idea,
      last_id: createEasyModeStore.lastRandomPromptId,
    })
    .then((res) => {
      renderData.randomLoading = false
      if (res.code == 200) {
        createEasyModeStore.lastRandomPromptId = res.data?.prompt?.id
        renderData.idea =
          res.data.prompt?.content?.slice(0, MAX_IDEA_COUNT) ?? ""
        createEasyModeStore.randomPromptData = res.data.prompt
        handleChange()
      }
    })
    .catch((err) => {
      renderData.randomLoading = false
    })

  globalProperties?.logClickEvent("create_prompt_random", {
    tab_stare: "basic_description",
    type: userDataStore.isLogin ? "sign_in" : "not_sign_in",
  })
}

watch(
  () => props.idea,
  (newVal) => {
    renderData.idea = props.idea
    renderData.count = renderData.idea.length
  }
)

watch(
  () => renderData.idea,
  (newVal) => {
    emits("input:idea", newVal)
  }
)

onMounted(() => {
  renderData.idea = props.idea
  renderData.count = renderData.idea.length
})

defineExpose({
  insertText(item) {
    // setAddRenderIdea(item);
    // nextTick(() => {
    //   refideaTextarea.value?.focus();
    // });
    renderData.idea = item?.slice(0, MAX_IDEA_COUNT)
    handleChange()
  },
})
</script>
<style lang="scss">
.edit-ideas {
  position: relative;
  padding: 17px 17px 12px 17px;
  border-radius: 12px;
  background: rgba(148, 173, 255, 0.2);
  border: none;
  position: relative;
  border: 1px solid rgba(255, 255, 255, 0);
  flex-direction: column;
  &:hover {
    background: rgba(148, 173, 255, 0.2);
  }
  &.edit-ideas-mode {
    border: 1px solid #94adff;
    &:hover {
      background: rgba(148, 173, 255, 0.2);
    }
    &:focus-within {
      background: rgba(148, 173, 255, 0.2);
    }
  }
  &.idea-edit-mode-active {
    background: rgba(148, 173, 255, 0.2);
  }
  &.idea-edit-mode-disabled {
    background: rgba(255, 255, 255, 0.1);
  }
  textarea {
    height: 100%;
    width: 100%;
    border: none;
    outline: none;
    resize: none;
    font-family: HarmonyOS Sans SC;
    font-size: 14px;
    font-weight: normal;
    color: #ffffff;
    line-height: 20px;
    background-color: transparent;
    &::placeholder {
      color: rgba(255, 255, 255, 0.5) !important;
    }
  }
  .edit-ideas-bottom {
    margin: 8px 0px 0px 0;
    align-items: end;
    justify-content: space-between;
  }
  .edit-ideas-count {
    font-family: HarmonyOS Sans SC;
    font-size: 12px;
    font-weight: 300;
    line-height: 16px;
    color: rgba(255, 255, 255, 0.5);
    padding-bottom: 4px;
  }
  .edit-ideas-count-error {
    color: #ff5f74;
  }
  .edit-ideas-random {
    height: 32px;
  }
  &.edit-ideas--mobile {
    padding: 14px 16px 12px 16px;
  }
}
</style>
